import type { InputHTMLAttributes, ReactNode } from "react";

import { CheckIcon } from "@/components/ui/icons";
import { cx } from "@/lib/cx";

/**
 * No `checkbox` entry exists in DESIGN.md yet — every checkbox in the
 * codebase before this component was a bare, unstyled native
 * `<input type="checkbox">` (live-caught on the Properties list's "Include
 * archived properties" toggle: 16x16px, no border, no radius, no accent —
 * under a third of DESIGN.md's own 44px tap-target minimum). This follows
 * the same token language `Input.tsx`/`Button.tsx` already use — accent
 * border/fill, `rounded.sm`, the global focus ring — rather than inventing a
 * parallel visual language for one control type.
 *
 * The native `<input>` stays the real, small (20px, `spacing-5`) focusable
 * element — a checkbox's focus ring is conventionally sized to the box
 * itself, not the tap target, matching every other browser/OS checkbox.
 *
 * Two exports: `CheckboxInput` is the bare styled control for callers that
 * already compose their own surrounding label/content (a row with a name
 * and description, a "3 of 5 selected" counter); `Checkbox` adds a real
 * 44x44 tap target and a visible text label for the common simple case.
 */
export function CheckboxInput({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <span className="relative inline-flex h-5 w-5 shrink-0 items-center justify-center">
      <input
        type="checkbox"
        className={cx(
          "peer h-5 w-5 shrink-0 appearance-none rounded-sm border border-border-strong bg-canvas checked:border-accent checked:bg-accent",
          className,
        )}
        {...props}
      />
      <CheckIcon
        aria-hidden="true"
        width={14}
        height={14}
        className="pointer-events-none absolute text-canvas opacity-0 peer-checked:opacity-100"
      />
    </span>
  );
}

export function Checkbox({
  className,
  children,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { children: ReactNode }) {
  return (
    <label className="inline-flex min-h-tap-target-min cursor-pointer items-center gap-2 text-body text-ink">
      <CheckboxInput className={className} {...props} />
      {children}
    </label>
  );
}
